/**
 * Module dependencies.
 */

import { AuthField, AuthShell, AuthSubmit } from 'app/components/pages/auth/auth-shell';
import { GoogleLogin } from '@react-oauth/google';
import { Logo } from 'app/components/pages/landing/brand';
import { appRoutes } from 'app/routes';
import { cookieStorage } from 'app/core/utils/cookie-storage';
import { useGoogleLogin } from 'app/hooks/requests/use-google-login';
import { useNavigate } from 'react-router-dom';
import { usePasswordLogin } from 'app/hooks/requests/use-password-login';
import { useResendVerification } from 'app/hooks/requests/use-resend-verification';
import React, { useEffect, useState } from 'react';

/**
 * Export `Home` component (sign-in page).
 */

export function Home(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notVerified, setNotVerified] = useState(false);
  const [resent, setResent] = useState(false);

  const goDashboard = () => navigate(appRoutes.dashboard.home.base);

  const { mutate: login } = useGoogleLogin({ onSuccess: goDashboard });
  const { mutate: resend } = useResendVerification({ onSuccess: () => setResent(true) });
  const { mutate: passwordLogin, isLoading } = usePasswordLogin({
    onError: (err: any) => {
      if (err?.response?.data?.data?.code === 'email_not_verified') {
        setNotVerified(true);
        setError('Please verify your email before signing in — check your inbox for the link.');

        return;
      }

      setError('Invalid email or password.');
    },
    onSuccess: goDashboard
  });

  useEffect(() => {
    if (cookieStorage.get('user')) {
      navigate(appRoutes.dashboard.home.base);
    }
  }, [navigate]);

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    setNotVerified(false);
    setResent(false);

    if (!email || !password) {
      return;
    }

    passwordLogin({ email, password });
  }

  return (
    <AuthShell>
      <div className={'flex justify-center'}>
        <Logo size={40} />
      </div>

      <h1 className={"mt-8 font-['Outfit'] text-3xl font-semibold tracking-[-0.03em]"}>{'Welcome back'}</h1>

      <p className={'mx-auto mt-2 max-w-xs text-sm text-neutral-500'}>
        {'Sign in to your Contab account to see your whole financial picture.'}
      </p>

      <form className={'mt-8 space-y-4'} onSubmit={onSubmit}>
        <AuthField autoComplete={'email'} label={'Email'} onChange={setEmail} type={'email'} value={email} />

        <AuthField
          autoComplete={'current-password'}
          label={'Password'}
          onChange={setPassword}
          type={'password'}
          value={password}
        />

        <div className={'-mt-1 text-right'}>
          <button
            className={'text-xs font-medium text-[#1B752B] hover:underline'}
            onClick={() => navigate(appRoutes.forgotPassword)}
            type={'button'}
          >
            {'Forgot password?'}
          </button>
        </div>

        {error && <p className={'text-left text-sm text-destructive'}>{error}</p>}

        {notVerified && (
          <button
            className={
              'h-10 w-full rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50 disabled:opacity-60'
            }
            disabled={resent}
            onClick={() => resend(email)}
            type={'button'}
          >
            {resent ? 'Verification email sent' : 'Resend verification email'}
          </button>
        )}

        <AuthSubmit disabled={isLoading}>{isLoading ? 'Signing in…' : 'Sign in'}</AuthSubmit>
      </form>

      <div className={'my-6 flex items-center gap-3 text-xs text-neutral-400'}>
        <span className={'h-px flex-1 bg-neutral-200'} />

        {'or'}

        <span className={'h-px flex-1 bg-neutral-200'} />
      </div>

      <div className={'flex justify-center'}>
        <GoogleLogin
          onError={() => setError('Google sign-in failed.')}
          onSuccess={credentialResponse => login(credentialResponse)}
          shape={'pill'}
          size={'large'}
          text={'continue_with'}
          theme={'outline'}
          width={'280'}
        />
      </div>

      <p className={'mt-8 text-sm text-neutral-500'}>
        {"Don't have an account? "}

        <button
          className={'font-medium text-[#1B752B] hover:underline'}
          onClick={() => navigate(appRoutes.register)}
          type={'button'}
        >
          {'Create one'}
        </button>
      </p>
    </AuthShell>
  );
}
